import { getAlertDetail } from '../services/allAlertsServices';
import { getReportBasicDetails } from '../services/reportServices';
import { decryptEurl } from '../services/singleReportService';
import { logger } from './logger';
import type { EventProperties } from './posthog';
import { captureEventInternal, getUserData } from './posthog';

export const EMAIL_LINK_CLICKED_EVENT = 'email_link_clicked';

const PARAM_TRACKING = 'trk';
const EMAIL_SOURCE = 'email';
const CAPTURED_KEY_PREFIX = 'emailLinkClicked:';

const TEMPLATE_ALERT_IMPACT_REPORT = 'ALERT_IMPACT_REPORT';
const TEMPLATE_PUBLISH_REPORT = 'PUBLISH_REPORT';

// emailTemplate (= mail_campaign upper-cased) -> emailCategory, matching the
// backend "email sent" events so both can be analyzed together.
const EMAIL_CATEGORY_BY_TEMPLATE: Record<string, string> = {
  [TEMPLATE_ALERT_IMPACT_REPORT]: 'Alerts',
  [TEMPLATE_PUBLISH_REPORT]: 'Publication Alerts',
};

// Decrypted contents of the `trk` token.
interface TrackingData {
  mail_campaign?: string;
  mail_link?: string;
  alertId?: string;
  reportId?: string;
  reportName?: string;
  product?: string;
}

export const hasEmailLinkParameters = (search: string): boolean =>
  new URLSearchParams(search).has(PARAM_TRACKING);

const isAlreadyCaptured = (signature: string): boolean => {
  try {
    return sessionStorage.getItem(CAPTURED_KEY_PREFIX + signature) !== null;
  } catch {
    return false;
  }
};

const markCaptured = (signature: string): void => {
  try {
    sessionStorage.setItem(CAPTURED_KEY_PREFIX + signature, '1');
  } catch {
    // storage unavailable - the in-component ref still prevents re-fires
  }
};

const toPositiveNumber = (value: string | number | null | undefined): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getUserProperties = (): EventProperties => {
  const userData = getUserData();
  return {
    userId: toPositiveNumber(userData?.userId),
    orgId: toPositiveNumber(userData?.orgId),
    emailAddress: userData?.emailAddress || undefined,
  };
};

// Properties for "Alerts" emails (ALERT_IMPACT_REPORT): alert-centric.
const buildAlertProperties = async (data: TrackingData): Promise<EventProperties> => {
  const properties: EventProperties = {
    alertId: data.alertId,
    products: data.product ? [data.product] : undefined,
  };

  const id = toPositiveNumber(data.alertId);
  if (!id) return properties;

  try {
    const response = await getAlertDetail(id);
    const detail = response.data;
    const products = (detail?.renderingStyleModelList ?? [])
      .map((style) => style.renderStyleName)
      .filter((name): name is string => !!name);
    return {
      ...properties,
      alertTitle: detail?.alertModel?.plainTitle ?? detail?.alertModel?.title,
      hasAttachment: (detail?.alertAttachmentModelList?.length ?? 0) > 0,
      products: products.length > 0 ? products : properties.products,
    };
  } catch (error) {
    // Enrichment is best-effort: still send the event with token data.
    logger.error('Email link tracking: failed to load alert details', error);
    return properties;
  }
};

// Properties for "Publication Alerts" emails (PUBLISH_REPORT): report-centric.
const buildPublicationReportProperties = async (
  data: TrackingData
): Promise<EventProperties> => {
  const properties: EventProperties = {
    reportId: data.reportId,
    reportName: data.reportName,
    products: data.product ? [data.product] : undefined,
  };

  const id = toPositiveNumber(data.reportId);
  if (!id) return properties;

  try {
    const response = await getReportBasicDetails(id);
    const detail = response.data;
    const products = (detail?.productModels ?? [])
      .map((product) => product.renderStyleName)
      .filter((name): name is string => !!name);
    return {
      ...properties,
      reportName: detail?.reportModel?.opinionName ?? properties.reportName,
      products: products.length > 0 ? products : properties.products,
    };
  } catch (error) {
    logger.error('Email link tracking: failed to load report details', error);
    return properties;
  }
};

const buildCampaignProperties = async (
  emailTemplate: string,
  data: TrackingData
): Promise<EventProperties> => {
  switch (emailTemplate) {
    case TEMPLATE_ALERT_IMPACT_REPORT: {
      return buildAlertProperties(data);
    }
    case TEMPLATE_PUBLISH_REPORT: {
      return buildPublicationReportProperties(data);
    }
    default: {
      return { alertId: data.alertId, reportId: data.reportId };
    }
  }
};

/**
 * Captures `email_link_clicked` for a URL carrying the encrypted `trk` token.
 * Must be called with the ORIGINAL search string: some landing pages
 * (e.g. /grid-view) redirect and drop the query string.
 */
export const captureEmailLinkClick = async (search: string): Promise<void> => {
  const token = new URLSearchParams(search).get(PARAM_TRACKING);
  if (!token) return;

  if (isAlreadyCaptured(search)) return;
  markCaptured(search);

  let data: TrackingData | null;
  try {
    data = (await decryptEurl(encodeURIComponent(token))) as unknown as TrackingData | null;
  } catch (error) {
    logger.error('Email link tracking: failed to decrypt trk', error);
    return;
  }
  if (!data) return;

  const emailTemplate = (data.mail_campaign ?? '').toUpperCase();

  void captureEventInternal(EMAIL_LINK_CLICKED_EVENT, {
    emailSource: EMAIL_SOURCE,
    emailTemplate: emailTemplate || undefined,
    emailCategory: EMAIL_CATEGORY_BY_TEMPLATE[emailTemplate],
    emailLink: data.mail_link,
    ...getUserProperties(),
    ...(await buildCampaignProperties(emailTemplate, data)),
  });
};
